import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useKeyboardInset } from "./use-keyboard-inset.ts";

/**
 * Карточка занятия: раскрытие занятия на месте.
 *
 * Пять решений, ни одно не косметическое.
 *
 * 1. **Карточка растёт ИЗ САМОЙ коробки занятия**, а не встаёт рядом с ней и
 *    тем более не под календарём. Общий с коробкой угол и есть вся связь
 *    «нажал здесь, раскрылось здесь же»: карточка сбоку от занятия эту связь
 *    держала слабее (глаз всё равно перескакивал через зазор), а карточка под
 *    сеткой не держала вовсе. Сетка при этом НЕ РАСТЯГИВАЕТСЯ: карточка лежит
 *    слоем поверх соседних колонок и ни на пиксель не двигает ни одну из них
 * 2. **Растёт в ту сторону, где есть место, и угол выбирается из четырёх.**
 *    Занятие в последней колонке раскрывается влево, занятие в 21:00 вверх, и
 *    в обоих случаях общий угол с коробкой сохраняется: он просто другой.
 *    Этот же угол уезжает в `data-grow` и становится точкой, из которой
 *    карточка вырастает (`transform-origin` в стилях)
 * 3. **Кнопок внутри коробки занятия НЕТ и не будет.** Высота коробки задана
 *    длительностью: занятие на тридцать минут это около тридцати пикселей, а
 *    при наложении двух занятий ширина колонки делится пополам. Кнопка туда не
 *    влезает ни при какой вёрстке, и это свойство раскладки, а не текущих
 *    отступов
 * 4. **На узком экране та же карточка приезжает снизу**, как в мобильных
 *    приложениях. Это ПОЛОЖЕНИЕ того же куска, а не второй компонент: два
 *    компонента разъехались бы содержимым в первую же правку
 * 5. **Положение считается от прямоугольника занятия в момент открытия**, а не
 *    задано в разметке. Тело календаря прокручивается само, и с 17.09.2026 по
 *    ОБЕИМ осям (`overflow: auto` у `.cal__body`: вбок узкая неделя, вниз часы),
 *    поэтому карточка живёт `position: fixed` в координатах окна: вложенная в
 *    прокручиваемый календарь, она обрезалась бы его краем
 *
 * ⚠️ Двойного нажатия здесь нет и не будет: на тач-экране его не существует, и
 * действие, доступное только им, на телефоне недоступно вовсе.
 */

/** Отступ карточки от края окна, когда места не хватило даже на рост. Пиксели. */
const EDGE_PX = 8;

/**
 * Ниже этой ширины карточка приезжает снизу.
 *
 * Величина та же, что у режима `day` в расписании: там, где семь колонок не
 * встают, не встаёт и карточка сбоку от колонки.
 */
const SHEET_MAX_WIDTH = 640;

/**
 * Угол коробки занятия, который карточка оставляет на месте и из которого
 * растёт. Первое слово это вертикаль, второе горизонталь.
 */
type Grow = "down-right" | "down-left" | "up-right" | "up-left";

interface Pos {
  left: number;
  top: number;
  grow: Grow;
}

/**
 * Прокручиваемые предки элемента.
 *
 * Нужны затем, что положение карточки считается от прямоугольника занятия, а
 * тот уезжает от любой прокрутки: и страницы, и самого календаря. Ищем по
 * вычисленному стилю, а не по классу `.cal`: класс это знание о чужой разметке,
 * которое разъедется при первой её правке.
 */
function scrollParents(el: HTMLElement | null): HTMLElement[] {
  const out: HTMLElement[] = [];
  let node = el?.parentElement ?? null;
  while (node !== null) {
    const style = getComputedStyle(node);
    const flow = `${style.overflowX} ${style.overflowY}`;
    if (flow.includes("auto") || flow.includes("scroll")) out.push(node);
    node = node.parentElement;
  }
  return out;
}

/**
 * Событие пришло из МОДАЛЬНОГО ОКНА, открытого ПОВЕРХ карточки.
 *
 * ⚠️ **Такое событие карточку не закрывает, ни мышью, ни клавишей.** Окно лежит
 * не мимо карточки, а над ней: нативный `<dialog>` с `showModal()` рисуется в
 * верхнем слое, фон под ним браузер помечает `inert`, но и `pointerdown`, и
 * `keydown` всплывают по обычному дереву и доходят до документа так, будто
 * человек нажал по расписанию.
 *
 * Поймано на имени ученика: оно открывает его карточку окном поверх сетки
 * (`CounterpartName` в `EntryCard.tsx`). Первое же нажатие внутри окна
 * сворачивало карточку занятия под ним, а Escape, которым окно закрывают чаще
 * всего, делал то же самое. Человек возвращался на расписание без той карточки,
 * из которой ушёл, то есть терял отметку о приходе, оплату и дверь в домашку, а
 * кнопка «Закрыть» в том же окне карточку сохраняла: два способа закрыть одно
 * окно давали разный исход.
 *
 * ⚠️ Слой опознаётся ближайшим `<dialog>` в предках, а не `:modal`: у
 * псевдокласса нужна проверка поддержки, а немодального `<dialog>` в проекте нет
 * вовсе, единственный примитив окна (`features/shell/Dialog.tsx`) зовёт
 * `showModal()`. С 14.09.2026 признак стал полным: своих модальных поверхностей
 * на `div` в приложении не осталось ни одной, и сторожит это
 * `lib/dialog-role.test.ts`.
 *
 * ⚠️ Нажатие МИМО модального окна тоже приходит отсюда: подложка это
 * псевдоэлемент `::backdrop` самого `<dialog>`, поэтому целью оказывается он.
 * Это и нужно: щелчок мимо окна закрывает окно, а карточка под ним остаётся.
 */
function fromDialogLayer(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("dialog") !== null;
}

export function EntryPopover({
  label,
  contentKey,
  revision,
  anchor,
  onClose,
  children,
}: {
  /** Имя поверхности для читалки: «Занятие 17:00». */
  label: string;
  /**
   * Ключ содержимого: занятия либо окна записи. Смена ключа пересоздаёт
   * карточку и пересчитывает место (разбор у `key` ниже).
   *
   * ⚠️ С 27.09.2026 поповер раскрывает и окно записи (`OpeningCard`), поэтому
   * карточку он получает детьми, а не рисует сам: положение, лист на телефоне,
   * нажатие мимо и возврат фокуса у обоих одни и те же.
   */
  contentKey: string;
  /**
   * Данные карточки как они есть: их смена (перечитанная неделя, занятие
   * переехало) пересчитывает место, как пересчитывала до обобщения, когда
   * зависимостью стояло само занятие.
   */
  revision: unknown;
  /** Коробка в календаре: от неё считается положение и в неё же возвращается фокус. */
  anchor: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [sheet, setSheet] = useState(
    () => typeof window !== "undefined" && window.innerWidth < SHEET_MAX_WIDTH,
  );
  const [pos, setPos] = useState<Pos | null>(null);
  const keyboard = useKeyboardInset(sheet);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${String(SHEET_MAX_WIDTH - 1)}px)`);
    const sync = () => {
      setSheet(mq.matches);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => {
      mq.removeEventListener("change", sync);
    };
  }, []);

  /**
   * Куда встать. Карточка накрывает коробку занятия и растёт от неё.
   *
   * По каждой оси выбор один и тот же: если карточка помещается, растя от
   * ближнего края коробки, растём от него; если нет, растём от дальнего, то
   * есть в другую сторону. Общий с коробкой угол при этом остаётся всегда, и
   * ровно он делает раскрытие раскрытием, а не появлением карточки где-то
   * поблизости.
   *
   * ⚠️ Карточка не имеет права уехать за край окна ни одной стороной: уехавшая
   * вниз выглядит как карточка без кнопок, а не как карточка, которую надо
   * прокрутить. Поэтому после выбора стороны идёт прижатие к окну, и оно же
   * последнее слово.
   */
  const place = useCallback(() => {
    const box = boxRef.current;
    if (box === null || anchor === null || sheet) return;
    const a = anchor.getBoundingClientRect();
    const w = box.offsetWidth;
    const h = box.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Вправо от левого края коробки, иначе влево от её правого края
    const right = a.left + w <= vw - EDGE_PX;
    let left = right ? a.left : a.right - w;
    left = Math.min(Math.max(EDGE_PX, left), Math.max(EDGE_PX, vw - w - EDGE_PX));

    // Вниз от верхнего края коробки, иначе вверх от её нижнего края
    const down = a.top + h <= vh - EDGE_PX;
    let top = down ? a.top : a.bottom - h;
    top = Math.min(Math.max(EDGE_PX, top), Math.max(EDGE_PX, vh - h - EDGE_PX));

    const grow: Grow = down
      ? right
        ? "down-right"
        : "down-left"
      : right
        ? "up-right"
        : "up-left";

    setPos((prev) =>
      prev !== null && prev.left === left && prev.top === top && prev.grow === grow
        ? prev
        : { left, top, grow },
    );
  }, [anchor, sheet]);

  // Слой, а не эффект: положение обязано быть посчитано ДО отрисовки, иначе
  // карточка на кадр появляется в углу и прыгает на место
  useLayoutEffect(() => {
    place();
  }, [place, revision]);

  useEffect(() => {
    if (sheet) return;
    const on = () => {
      place();
    };
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on);
    /*
      ⚠️ Прокручиваемые предки занятия слушаются ПОИМЁННО, а не через
      перехватывающий обработчик на окне. Тело календаря прокручивается само, и с
      17.09.2026 по ОБЕИМ осям (`overflow: auto` на `.cal__body`), а его
      прокрутка на окне не ловится: событие прокрутки не всплывает, а перехват на
      окне в проверке на стенде не сработал ни разу. Слушая сам прокручиваемый
      элемент, мы получаем событие там, где оно и происходит. ⚠️ Слушать окно всё
      равно надо: на расписании страница с того же дня не прокручивается вовсе, а
      на других экранах карточки этой нет, зато `resize` приходит только оттуда.

      ⚠️ Оба переезда прокрутки 17.09.2026 (сначала вбок с `.cal` на
      `.cal__body`, потом вертикаль со страницы туда же) здесь не стоили ни
      строки, и это не везение: `scrollParents` ищет предков по вычисленному
      стилю, а не по классу и не по оси, поэтому и новый узел, и новую ось она
      нашла сама.
    */
    const scrollers = scrollParents(anchor);
    for (const el of scrollers) el.addEventListener("scroll", on);
    /*
      Карточка меняет высоту сама: раскрылась форма переноса, приехала красная
      строка отказа. Без наблюдателя выросшая вниз карточка вылезла бы за низ
      окна ровно в тот момент, когда в ней появились кнопки
    */
    const ro = new ResizeObserver(on);
    const box = boxRef.current;
    if (box !== null) ro.observe(box);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("scroll", on);
      for (const el of scrollers) el.removeEventListener("scroll", on);
      ro.disconnect();
    };
  }, [anchor, place, sheet]);

  /**
   * Escape закрывает, фокус возвращается на занятие.
   *
   * ⚠️ Возврат фокуса обязателен, а не вежливость: человек, пришедший сюда с
   * клавиатуры, после закрытия остался бы с фокусом на `body` и пошёл бы
   * табуляцией с самого начала страницы.
   *
   * ⚠️ **Escape, нажатый в открытом поверх карточки окне, закрывает только
   * окно.** Человек жмёт его, чтобы вернуться к занятию, а не чтобы уйти с
   * расписания, и правило здесь ровно то же, что у нажатия мышью ниже: разбор
   * в `fromDialogLayer`. Раньше тут стояло обратное, и «выйти отсюда целиком»
   * оказалось нашим прочтением, а не человеческим: кнопка «Закрыть» в том же
   * окне карточку сохраняла, а клавиша теряла.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || fromDialogLayer(e.target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  /**
   * ⚠️ Фокус берётся ПОСЛЕ того, как карточка встала на место, и это не
   * придирка к порядку. До первого замера она скрыта `visibility: hidden`
   * (иначе кадр в углу экрана), а скрытый элемент сфокусировать нельзя вовсе:
   * `focus()` на нём молча ничего не делает. Поймано на стенде, где карточка
   * открывалась, а фокус оставался на коробке занятия.
   */
  const placed = sheet || pos !== null;
  useEffect(() => {
    if (!placed) return;
    boxRef.current?.focus({ preventScroll: true });
  }, [anchor, placed]);

  /**
   * Возврат фокуса на занятие при закрытии.
   *
   * ⚠️ Пустые зависимости намеренно: фокус возвращается на ЗАКРЫТИИ, а не при
   * переходе к соседнему занятию. Иначе нажатие по другому занятию сначала
   * увело бы фокус на прежнюю коробку, и человек с клавиатурой оказался бы не
   * там, куда нажал. Последний якорь берётся из ссылки.
   *
   * ⚠️ Возврат отложен на такт и делается ТОЛЬКО если фокус никто не забрал.
   * Браузер по нажатию мыши уводит фокус на `body` уже после наших эффектов, и
   * немедленный возврат он бы просто перебил (проверено на стенде). А проверка
   * на `body` нужна ровно затем, чтобы не отобрать фокус у того, по чему
   * человек нажал: закрыть карточку можно и нажатием на ссылку в меню.
   */
  const anchorRef = useRef(anchor);
  useEffect(() => {
    anchorRef.current = anchor;
  }, [anchor]);
  useEffect(
    () => () => {
      const last = anchorRef.current;
      // Занятие могло исчезнуть из сетки вместе с закрытием (отмена разового
      // уносит урок в корзину), и тогда возвращать фокус некуда
      if (last === null || !last.isConnected) return;
      setTimeout(() => {
        const active = document.activeElement;
        if (active === null || active === document.body) last.focus({ preventScroll: true });
      }, 0);
    },
    [],
  );

  /**
   * Нажатие мимо закрывает.
   *
   * ⚠️ Нажатие по ЛЮБОМУ занятию календаря пропускается: его собственный
   * обработчик либо переключит карточку на другое занятие, либо закроет эту.
   * Без этого исключения повторное нажатие по тому же занятию закрывало бы
   * карточку здесь и тут же открывало её обратно там.
   */
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (boxRef.current?.contains(t) === true) return;
      if (t.closest(".cal__ev, .cal__win") !== null) return;
      // Нажатие внутри окна, открытого ПОВЕРХ карточки, её не закрывает: это не
      // «мимо», это «над». Разбор целиком в `fromDialogLayer`
      if (fromDialogLayer(t)) return;
      /**
       * Нажатие по фону (затемнение снизу, пустая клетка календаря) только
       * ЗАКРЫВАЕТ карточку, и дальше не идёт. Иначе одно нажатие делало бы два
       * дела сразу: закрывало карточку и открывало диалог создания занятия на
       * клетке, по которой человек нажал, только чтобы карточку убрать.
       *
       * Прочие нажатия (кнопка в боковом меню, ссылка) не трогаем: там первое
       * нажатие обязано сработать с первого раза.
       */
      if (t.classList.contains("pop__scrim") || t.classList.contains("cal__slot")) {
        e.preventDefault();
      }
      onClose();
    };
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("pointerdown", onDown);
    };
  }, [onClose]);

  return (
    <>
      {/*
        Затемнение только у нижнего положения: там карточка занимает пол-экрана
        и отделить её от расписания надо. Сбоку затемнение спрятало бы ровно ту
        сетку, ради связи с которой карточка и переехала к занятию
      */}
      {sheet && <div className="pop__scrim" aria-hidden="true" />}
      <div
        ref={boxRef}
        className="pop"
        data-sheet={sheet}
        // Угол, из которого карточка выросла. Стили превращают его в точку
        // роста: без этого раскрытие читается как выпрыгивание из центра
        data-grow={sheet ? undefined : pos?.grow}
        role="dialog"
        aria-label={label}
        tabIndex={-1}
        style={
          sheet
            ? /* Высота клавиатуры в переменной, а не в раскладке: лист прижат к
                 низу окна, и поднять его над клавиатурой умеет только CSS,
                 потому что `bottom` у него задан правилом. Разбор в
                 `useKeyboardInset` */
              ({ "--kb": `${String(keyboard)}px` } as React.CSSProperties)
            : {
                left: `${String(pos?.left ?? 0)}px`,
                top: `${String(pos?.top ?? 0)}px`,
                // До первого замера карточка невидима: замерять её надо
                // отрисованной, а показывать посчитанной
                visibility: pos === null ? "hidden" : "visible",
              }
        }
      >
        {/*
          ⚠️ **`key` тут обязателен, и это не оптимизация, а починка.** Карточка
          держит набранное человеком: адрес созвона в раскрытой форме, причину
          отмены, красную строку отказа. Переход к соседнему занятию нажатием по
          сетке карточку НЕ пересоздаёт (обработчик нажатия мимо пропускает
          нажатия по занятиям намеренно), поэтому без ключа набранный у занятия A
          адрес оказывался в форме занятия B и сохранялся ему: `ExternalCallForm`
          заводит значение поля инициализатором `useState`, а он на смену пропсов
          не смотрит. Ключ занятия делает переход тем, чем человек его и считает:
          это другая карточка, а не та же самая с другими словами.
        */}
        <Fragment key={contentKey}>{children}</Fragment>
      </div>
    </>
  );
}
